// OpenDB South African places reference — browser-side loader + lazy wrappers.
//
// The pure lookup logic lives in `@rosta/business-functions/geocoding`
// (framework-agnostic, shared with the backend). This module adds the
// browser-only dataset loader (`loadPlaces`, which fetches the bundled
// OpenDB JSON) and the async convenience wrappers used by the UI.

export * from '@rosta/business-functions/geocoding';

import type { OpenDBPlace, NamedPlace, NearestPlace } from '@rosta/business-functions/geocoding';
import { nameToCoordsFrom, nearestPlaceFrom, searchPlacesFrom, provinceOfFrom } from '@rosta/business-functions/geocoding';

let cache: OpenDBPlace[] | null = null;
let inflight: Promise<OpenDBPlace[]> | null = null;

export async function loadPlaces(): Promise<OpenDBPlace[]> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = fetch(`${import.meta.env.BASE_URL}geolocation/opendb.json`)
    .then((r) => {
      if (!r.ok) throw new Error(`Failed to load places: ${r.status}`);
      return r.json() as Promise<OpenDBPlace[]>;
    })
    .then((data) => {
      cache = data;
      inflight = null;
      return data;
    });
  return inflight;
}

/**
 * Forward lookup that lazy-loads the dataset. See {@link nameToCoordsFrom} for
 * the matching rules.
 */
export async function nameToCoords(query: string): Promise<NamedPlace | null> {
  const places = await loadPlaces();
  return nameToCoordsFrom(places, query);
}

/**
 * Reverse-lookup that lazy-loads the dataset. See {@link nearestPlaceFrom} for
 * the selection rules.
 */
export async function coordsToNearestPlace(
  lat: number,
  lon: number,
): Promise<NearestPlace | null> {
  const places = await loadPlaces();
  return nearestPlaceFrom(places, lat, lon);
}

/**
 * Forward autocomplete that lazy-loads the in-browser dataset. See
 * {@link searchPlacesFrom} for the matching rules.
 */
export async function searchPlaces(query: string, limit = 12): Promise<NamedPlace[]> {
  const places = await loadPlaces();
  return searchPlacesFrom(places, query, limit);
}

/** Lazy-loading variant of {@link provinceOfFrom}. */
export async function provinceOf(place: OpenDBPlace): Promise<string | null> {
  const places = await loadPlaces();
  return provinceOfFrom(places, place);
}
